import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { VehicleModelForm } from "./VehicleModelForm";
import Modal from "@/components/ui/Modal";
import {
  useCreateVehicleModel,
  useUpdateVehicleModel,
  useVehicleModelForm,
} from "@/hooks/useVehicleModels";
import { VehicleModel, CreateVehicleModelRequest } from "@/types";

interface VehicleModelModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleModel?: VehicleModel;
  mode: "create" | "edit" | "view";
}

export const VehicleModelModal: React.FC<VehicleModelModalProps> = ({
  isOpen,
  onClose,
  vehicleModel,
  mode,
}) => {
  const createVehicleModelMutation = useCreateVehicleModel();
  const updateVehicleModelMutation = useUpdateVehicleModel();
  const { t } = useTranslation(["pages", "common"]);

  const { vehicleModelData, errors, validate, updateField, resetForm } =
    useVehicleModelForm(vehicleModel);

  useEffect(() => {
    if (isOpen && vehicleModel) {
      resetForm();
    }
  }, [isOpen, vehicleModel, resetForm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      if (mode === "create") {
        await createVehicleModelMutation.mutateAsync(
          vehicleModelData as CreateVehicleModelRequest,
        );
      } else if (mode === "edit" && vehicleModel) {
        await updateVehicleModelMutation.mutateAsync({
          id: vehicleModel.id,
          ...vehicleModelData,
        });
      }

      onClose();
    } catch (error) {
      console.error("Error saving vehicle model:", error);
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const getModalTitle = () => {
    switch (mode) {
      case "create":
        return t("pages:vehicles.models.modalCreateTitle");
      case "edit":
        return t("pages:vehicles.models.modalEditTitle");
      case "view":
        return t("pages:vehicles.models.modalViewTitle");
      default:
        return t("pages:vehicles.models.modalDefaultTitle");
    }
  };

  const formatFullName = (maker: string, model: string, year: number) => {
    return `${year} ${maker} ${model}`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={getModalTitle()} size="lg">
      {mode === "view" && vehicleModel ? (
 <div className="space-y-6">
 <div className="bg-secondary-50 rounded-lg p-6">
 <h3 className="text-lg font-semibold text-secondary-900 mb-4">
              {formatFullName(
                vehicleModel.maker,
                vehicleModel.model,
                vehicleModel.year,
              )}
            </h3>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
  <div>
  <p className="text-sm font-medium text-secondary-700">{t("pages:vehicles.models.viewFields.maker")}</p>
  <p className="text-secondary-900">{vehicleModel.maker}</p>
              </div>
  <div>
  <p className="text-sm font-medium text-secondary-700">{t("pages:vehicles.models.viewFields.model")}</p>
  <p className="text-secondary-900">{vehicleModel.model}</p>
              </div>
  <div>
  <p className="text-sm font-medium text-secondary-700">{t("pages:vehicles.models.viewFields.year")}</p>
  <p className="text-secondary-900">{vehicleModel.year}</p>
              </div>
            </div>

            {vehicleModel.description && (
 <div className="mb-4">
  <p className="text-sm font-medium text-secondary-700 mb-2">
                  {t("common:description")}
                </p>
 <p className="text-secondary-900">{vehicleModel.description}</p>
              </div>
            )}

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-secondary-600">
              <div>
  <p className="font-medium">{t("pages:vehicles.models.created")}</p>
                <p>{new Date(vehicleModel.createdAt).toLocaleDateString()}</p>
              </div>
              {vehicleModel.updatedAt &&
                vehicleModel.updatedAt !== vehicleModel.createdAt && (
                  <div>
  <p className="font-medium">{t("pages:vehicles.models.lastUpdated")}</p>
                    <p>
                      {new Date(vehicleModel.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                )}
            </div>
          </div>

 <div className="flex justify-end">
            <button
              onClick={onClose}
               className="px-4 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 transition-colors"
             >
               {t("common:close")}
             </button>
          </div>
        </div>
      ) : (
        <VehicleModelForm
          vehicleModelData={vehicleModelData}
          errors={errors}
          onUpdateField={updateField}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={
            createVehicleModelMutation.isPending ||
            updateVehicleModelMutation.isPending
          }
          isEditing={mode === "edit"}
        />
      )}
    </Modal>
  );
};
